import React, { useState, useEffect, useCallback } from 'react';
import { devLog } from './utils/logger';
import { getMonthlyMotto, fetchCurrentMotto } from './utils/monthlyMottos';

// Configuración de la API - Detectar si estamos en app nativa (Capacitor)
const isNative = window.Capacitor?.isNativePlatform?.() || false;
const API_BASE_URL = isNative 
  ? 'https://api.appagustinos.es/api'  // App nativa - URL directa al servidor
  : (process.env.REACT_APP_API_URL || '/api');  // PWA - Proxy de Vercel

// Componente principal
const Header = ({ currentView, currentSection, teInteresaArticles = [], onArticleClick, onSectionClick }) => {
  const [showNotification, setShowNotification] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [eventosDelDia, setEventosDelDia] = useState({
    teInteresa: null,
    santoAgustino: null,
    santoral: null
  });
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [monthlyMotto, setMonthlyMotto] = useState(getMonthlyMotto());

  const getCurrentDate = () => {
    const now = new Date();
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${days[now.getDay()]}, ${now.getDate()} ${months[now.getMonth()]}`;
  };

  // Función para cargar TODOS los eventos del día
  const cargarEventosDelDia = useCallback(async () => {
    try {
      setLoadingEvents(true);
      
      const hoy = new Date();
      const mesHoy = hoy.getMonth();
      const diaHoy = hoy.getDate();
      const añoHoy = hoy.getFullYear();
      const fechaHoy = `${añoHoy}-${String(mesHoy + 1).padStart(2, '0')}-${String(diaHoy).padStart(2, '0')}`;
      
      const eventos = {
        teInteresa: null,
        santoAgustino: null,
        santoral: null
      };

      // 1. BUSCAR EN "TE INTERESA" (Días internacionales con artículos)
      if (teInteresaArticles && teInteresaArticles.length > 0) {
        const eventosTeInteresa = teInteresaArticles.filter(articulo => {
          const titulo = articulo.title || '';
          return titulo.toLowerCase().startsWith('día') || titulo.toLowerCase().startsWith('jornada');
        });
        
        const eventoTeInteresaHoy = eventosTeInteresa.find(articulo => {
          const fechaArticulo = new Date(articulo.date);
          return fechaArticulo.getMonth() === mesHoy && 
                 fechaArticulo.getDate() === diaHoy &&
                 fechaArticulo.getFullYear() === añoHoy;
        });

        if (eventoTeInteresaHoy) {
          eventos.teInteresa = {
            title: eventoTeInteresaHoy.title,
            article: eventoTeInteresaHoy,
            clickeable: true
          };
          devLog(`Header - Día internacional encontrado: ${eventoTeInteresaHoy.title}`);
        }
      }

      // 2. BUSCAR EN "SANTOS AGUSTINOS"
      const appData = window.agustinosAppData;
      if (appData && appData.sections && appData.sections['Santos Agustinos']) {
        let santos = appData.sections['Santos Agustinos'];
        
        // Si hay pocos santos, intentar cargar sección completa
        if (santos.length < 30 && appData.loader && appData.loader.cargarSeccionCompleta) {
          try {
            const santosCompletos = await appData.loader.cargarSeccionCompleta('Santos Agustinos', 500);
            if (santosCompletos.length > santos.length) {
              santos = santosCompletos;
              appData.sections['Santos Agustinos'] = santos;
            }
          } catch (error) {
            devLog('Header - Error cargando santos completos:', error);
          }
        }

        // Buscar santo del día
        const santoHoy = santos.find(santo => {
          const fechaSanto = new Date(santo.date);
          return fechaSanto.getMonth() === mesHoy && 
                 fechaSanto.getDate() === diaHoy &&
                 fechaSanto.getFullYear() === añoHoy;
        });

        if (santoHoy) {
          eventos.santoAgustino = {
            title: santoHoy.title,
            article: santoHoy,
            clickeable: true
          };
          devLog(`Header - Santo Agustino encontrado: ${santoHoy.title}`);
        }
      }

      // 3. BUSCAR SANTORAL EN BACKEND
      try {
        const response = await fetch(`${API_BASE_URL}/calendar/events/date/${fechaHoy}`);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.data.events && data.data.events.length > 0) {
            // Buscar santoral (tipo 'santoral')
            const santoralEvento = data.data.events.find(e => e.event_type === 'santoral');
            
            if (santoralEvento) {
              eventos.santoral = {
                title: santoralEvento.title,
                article: null,
                clickeable: false
              };
              devLog(`Header - Santoral encontrado: ${santoralEvento.title}`);
            }

            // Si NO hay evento de Te interesa con artículo, buscar evento backend tipo 'evento'
            if (!eventos.teInteresa) {
              const eventoBackend = data.data.events.find(e => e.event_type === 'evento');
              
              if (eventoBackend) {
                eventos.teInteresa = {
                  title: eventoBackend.title,
                  article: null,
                  clickeable: false
                };
                devLog(`Header - Día internacional (backend) encontrado: ${eventoBackend.title}`);
              }
            }
          }
        }
      } catch (error) {
        devLog('Header - Error cargando eventos del backend:', error);
      }

      setEventosDelDia(eventos);
      devLog('Header - Eventos del día cargados:', eventos);

    } catch (error) {
      console.error('❌ Header - Error cargando eventos del día:', error);
    } finally {
      setLoadingEvents(false);
    }
  }, [teInteresaArticles]);

  // Cargar eventos al montar o cuando cambien los artículos
  useEffect(() => {
    const timer = setTimeout(() => {
      cargarEventosDelDia();
    }, 500);

    return () => clearTimeout(timer);
  }, [cargarEventosDelDia]);

  // Lema mensual desde el backend (con cache y fallback local ya inicializado).
  useEffect(() => {
    let cancelled = false;
    fetchCurrentMotto().then((text) => {
      if (!cancelled && text) setMonthlyMotto(text);
    });
    return () => { cancelled = true; };
  }, []);

  // Determinar color del punto según prioridad
  const getIndicatorColor = () => {
    if (eventosDelDia.teInteresa && eventosDelDia.teInteresa.clickeable) {
      return 'bg-green-500'; // Verde - Día internacional con artículo
    }
    if (eventosDelDia.santoAgustino && eventosDelDia.santoAgustino.clickeable) {
      return 'bg-red-500'; // Rojo - Santo Agustino con artículo
    }
    if (eventosDelDia.santoral) {
      return 'bg-blue-500'; // Azul - Santoral (solo informativo)
    }
    return null; // Sin punto si no hay eventos
  };

  // Verificar si hay algún evento
  const hayEventos = () => {
    return eventosDelDia.teInteresa || eventosDelDia.santoAgustino || eventosDelDia.santoral;
  };

  const handleDateClick = () => {
    if (hayEventos() && !loadingEvents) {
      setMounted(true);
      setTimeout(() => setShowNotification(true), 10);
      setTimeout(() => {
        setShowNotification(false);
        setTimeout(() => setMounted(false), 600);
      }, 5000);
    }
  };

  // Función para manejar click en evento
  const handleEventClick = (evento) => {
    if (evento && evento.clickeable && evento.article && onArticleClick) {
      console.log(`🎯 Navegando al artículo: ${evento.title}`);
      
      // Determinar sección según el tipo de evento
      const seccion = eventosDelDia.teInteresa === evento ? 'Te interesa' : 'Santos Agustinos';
      
      onArticleClick(evento.article, seccion);
      setShowNotification(false);
      setMounted(false);
    }
  };

  const indicatorColor = getIndicatorColor();

  return (
    <div className="relative">
      {/* Fondo extendido que cubre la safe area - Optimizado para iOS/Android PWA */}
      <div 
        className="absolute inset-x-0 top-0"
        style={{
          background: 'linear-gradient(to bottom, #c82318 0%, #c82318 15%, #a01916 100%)',
          height: '100%',
          minHeight: 'calc(100% + env(safe-area-inset-top))',
          marginTop: 'calc(-1 * env(safe-area-inset-top))',
          paddingTop: 'env(safe-area-inset-top)',
          zIndex: 0
        }}
      />
      
      {/* Notificación flotante con transición suave
          Se coloca fuera del <header overflow-hidden> para que no se recorte
          al expandirse con muchos eventos y quede por encima de las pills */}
      {mounted && (
        <div className={`absolute left-1/2 transform -translate-x-1/2 bg-white text-gray-800 px-4 py-3 rounded-b-2xl z-40 shadow-xl
          transition-all duration-500 ease-in-out
          ${showNotification ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}
        `}
          style={{
            top: 'max(env(safe-area-inset-top), 20px)',
            width: '97%',
            fontSize: '13px',
            fontWeight: '500',
            letterSpacing: '0.02em',
            lineHeight: '1.5',
            border: '2px solid rgba(230, 44, 25, 0.1)',
            backdropFilter: 'blur(10px)',
            transformOrigin: 'top center'
          }}
        >
          {/* Título con fecha */}
          <div className="text-center mb-3 pb-2 border-b border-gray-200">
            <span className="text-sm font-semibold text-gray-600">
              📅 Hoy {getCurrentDate()}
            </span>
          </div>

          {/* Lista de eventos */}
          <div className="space-y-2">
            {/* Día internacional (verde) */}
            {eventosDelDia.teInteresa && (
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
                <span 
                  className={`flex-1 ${eventosDelDia.teInteresa.clickeable ? 'text-red-600 font-semibold cursor-pointer hover:underline' : 'text-gray-700'}`}
                  onClick={() => handleEventClick(eventosDelDia.teInteresa)}
                >
                  {eventosDelDia.teInteresa.title}
                </span>
              </div>
            )}

            {/* Santo Agustino (rojo) */}
            {eventosDelDia.santoAgustino && (
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0" />
                <span 
                  className={`flex-1 ${eventosDelDia.santoAgustino.clickeable ? 'text-red-600 font-semibold cursor-pointer hover:underline' : 'text-gray-700'}`}
                  onClick={() => handleEventClick(eventosDelDia.santoAgustino)}
                >
                  {eventosDelDia.santoAgustino.title}
                </span>
              </div>
            )}

            {/* Santoral (azul) */}
            {eventosDelDia.santoral && (
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                <span className="flex-1 text-gray-700">
                  {eventosDelDia.santoral.title}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header con contenido dentro de safe area */}
      <header 
        className="relative overflow-hidden"
        style={{ 
          color: 'white',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: '15px',
          paddingLeft: '20px',
          paddingRight: '20px',
          minHeight: '110px'
        }}
      >

        {/* Fondo flotante decorativo */}
        <div 
          className="absolute -top-1/2 -right-1/2 w-full h-full opacity-10 animate-float pointer-events-none"
          style={{
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            zIndex: 1
          }}
        />

        {/* Contenido principal del header */}
        <div className="relative z-10" style={{ paddingTop: '10px' }}>
          {/* Barra superior con logo y fecha */}
          <div className="flex justify-between items-center mb-4">
            {/* Logo Agustinos - Izquierda */}
            <div className="flex items-center">
              <img 
                src="/assets/logo-agustinos.png" 
                alt="Agustinos" 
                className="h-9 w-auto opacity-100 transition-opacity"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>

            {/* Fecha clickeable - Derecha */}
            <div 
              onClick={handleDateClick} 
              className="glass-effect px-4 py-2 rounded-full text-sm font-semibold cursor-pointer hover:bg-white/30 transition-all duration-200 transform hover:scale-105 relative"
              style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)' }}
            >
              {getCurrentDate()}
              {/* Punto de color cuando hay evento */}
              {indicatorColor && (
                <div 
                  className={`absolute top-0 right-1 w-3 h-3 ${indicatorColor} rounded-full border-2 border-white animate-pulse`}
                  style={{ boxShadow: '0 0 6px rgba(0, 0, 0, 0.3)' }}
                />
              )}
            </div>
          </div>

          {/* Título principal */}
          <div className="text-center" style={{ marginBottom: '8px' }}>
            {currentView === 'main' ? (
              <h1 
                className="text-2xl font-bold mb-0" 
                style={{ 
                  fontFamily: "'Guthen Bloots', cursive",
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                  letterSpacing: '0.02em',
                  lineHeight: '1.2',
                  marginBottom: '0',
                  fontSize: '1.75rem'
                }}
              >
                {monthlyMotto}
              </h1>
            ) : (
              <>
                <h1 
                  className="text-xl font-bold mb-1" 
                  style={{ 
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    lineHeight: '1.2'
                  }}
                >
                  {currentSection || 'Somos Comunidad Acogedora'}
                </h1>
                <p 
                  className="text-white/80 text-sm"
                  style={{ 
                    lineHeight: '1.3',
                    marginTop: '4px'
                  }}
                >
                  {currentView === 'list' && 'Lista de artículos'}
                  {currentView === 'detail' && 'Detalle del artículo'}
                  {currentView === 'social' && 'Redes sociales'}
                  {currentView === 'ven-y-veras' && 'Ven y verás'}
                </p>
              </>
            )}
          </div>
        </div>
      </header>
    </div>
  );
};

export default Header;